import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import _ from 'lodash';
import { ImageMedia } from 'src/models/media.interface';
import { defaultPagination, Pagination } from 'src/models/pagination';
import { defaultStatus, SliceStatus } from 'src/models/status';
import type { Workspace } from 'src/models/workspace.interface';
import {
  defaultWorkspaceFilterState,
  WorkspaceFilter
} from 'src/models/workspaceFilter';
import type { AppThunk } from 'src/store';
import { resetFilters } from 'src/store/extraActions';
import axiosCore from 'src/utils/axiosCore';
import { v4 as uuidv4 } from 'uuid';

interface WorkspaceState {
  workspaces: Workspace[];
  pagination: Pagination;
  filter: WorkspaceFilter;
  reload: boolean;
  status: SliceStatus;
}

const initialState: WorkspaceState = {
  workspaces: [],
  filter: defaultWorkspaceFilterState,
  pagination: defaultPagination,
  reload: false,
  status: defaultStatus
};

// This is used in internal workspace management only.
const slice = createSlice({
  name: 'workspace',
  initialState,
  reducers: {
    getWorkspaces(
      state: WorkspaceState,
      action: PayloadAction<{ workspaces: Workspace[]; pagination: Pagination }>
    ) {
      const { workspaces, pagination } = action.payload;
      state.workspaces = workspaces;
      state.pagination = pagination;
      state.reload = false;
      state.status = { ...defaultStatus };
    },
    createWorkspace(state: WorkspaceState, action: PayloadAction<Workspace>) {
      const workspace = action.payload;
      state.workspaces = [...state.workspaces, workspace];
      state.status = { ...defaultStatus };
    },
    selectWorkspace(state: WorkspaceState, action: PayloadAction<Workspace>) {
      const workspace = action.payload;
      state.workspaces = [
        ...state.workspaces.filter((r) => r._id !== workspace._id),
        workspace
      ];
      state.status = { ...defaultStatus };
    },
    updateFilter(
      state: WorkspaceState,
      action: PayloadAction<{ filter: WorkspaceFilter }>
    ) {
      const { filter } = action.payload;
      state.filter = { ...state.filter, page: 1, ...filter };
    },
    updateWorkspace(state: WorkspaceState, action: PayloadAction<Workspace>) {
      const workspace = action.payload;
      state.workspaces = _.map(state.workspaces, (_workspace) => {
        if (_workspace._id === workspace._id) {
          return workspace;
        }
        return _workspace;
      });
      state.status = { ...defaultStatus };
    },
    addMedia(
      state: WorkspaceState,
      action: PayloadAction<{ workspaceId: string; data: ImageMedia }>
    ) {
      const { workspaceId, data } = action.payload;
      let workspace = state.workspaces.find(
        (workspace) => workspace._id === workspaceId
      ) as any;
      if (!workspace) {
        workspace = { _id: workspaceId };
      }
      state.workspaces = _.map(state.workspaces, (_workspace) => {
        if (_workspace._id === workspace._id) {
          const images = [
            ...workspace.images,
            {
              _id: data._id,
              image: '',
              imageUrl: data.imageUrl,
              mediaType: data.mediaType
            }
          ];
          return { ...workspace, images };
        }
        return _workspace;
      });
      state.status = { ...defaultStatus };
    },
    requestFailed(state: WorkspaceState, action: PayloadAction<string>) {
      state.status = { message: action.payload, variant: 'error' };
    }
  },
  extraReducers: {
    // resets this slice when changing workspaces
    [resetFilters.type]() {
      return initialState;
    }
  }
});

export const reducer = slice.reducer;

export const getWorkspaces =
  (filter: WorkspaceFilter): AppThunk =>
  async (dispatch) => {
    let response;
    try {
      response = await axiosCore.post<{ data: any }>(
        `/workspace/filter`,
        filter
      );
    } catch (error) {
      dispatch(slice.actions.requestFailed(error.message));
      return;
    }
    dispatch(slice.actions.getWorkspaces(response.data.data));
  };

export const createWorkspace =
  (data: any): AppThunk =>
  async (dispatch) => {
    const response = await axiosCore.post<{ data: any }>(`/workspace`, data);
    dispatch(slice.actions.createWorkspace(response.data.data));
  };

export const getWorkspace =
  (workspaceId: string): AppThunk =>
  async (dispatch) => {
    let response;
    try {
      response = await axiosCore.get<{ data: any }>(
        `/workspace/${workspaceId}`
      );
    } catch (error) {
      dispatch(slice.actions.requestFailed(error.message));
      return;
    }
    dispatch(slice.actions.selectWorkspace(response.data.data));
  };

export const updateWorkspaceFilter =
  (filter: WorkspaceFilter): AppThunk =>
  async (dispatch) => {
    dispatch(slice.actions.updateFilter({ filter }));
  };

export const updateWorkspace =
  (workspaceId: string, update: any): AppThunk =>
  async (dispatch) => {
    const response = await axiosCore.put<{ data: any }>(
      `/workspaces/${workspaceId}`,
      {
        ...update
      }
    );
    dispatch(slice.actions.updateWorkspace(response.data.data));
  };

export const updateWorkspaceCustom =
  (workspaceId: string, update: Partial<Workspace['custom']>): AppThunk =>
  async (dispatch) => {
    const response = await axiosCore.put<{ data: any }>(
      `/workspaces/${workspaceId}/custom`,
      {
        ...update
      }
    );
    dispatch(slice.actions.updateWorkspace(response.data.data));
  };

export const addMedia =
  (workspaceId: string, file: File): AppThunk =>
  async (dispatch) => {
    let response;
    try {
      const formData = new FormData();
      const imageName = `cptr-${uuidv4()}-${Date.now().toString()}`;

      formData.append('folder', 'profile');
      formData.append('photo', file, imageName);
      formData.append('mediaType', 'photo');

      response = await axiosCore.post<{ data: ImageMedia }>(
        `/media`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
    } catch (error) {
      dispatch(slice.actions.requestFailed(error.message));
      return;
    }
    dispatch(slice.actions.addMedia({ workspaceId, data: response.data.data }));
  };
